import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { memoForwardRef } from "@shared/client/util/memo";
import { FaIcon } from "@shared/client/components/fa-icon";
import { faCheck, faThumbtack } from "@fortawesome/pro-solid-svg-icons";
import { showToast } from "@shared/client/components/toast/show-toast";
import { MAX_PINS_PER_SURFACE } from "@shared/types/pin";
import { BlockMiniCapture } from "@/components/block-mini-capture/block-mini-capture";
import { getCollectionCardColors } from "@/config/block-styles";
import { collectionStore } from "@/data/collection-store";
import { togglePinWithSync, usePinList } from "@/state/pin-store";
import { useApp } from "@/state/app-context";
import { useLongPressCollectionSelect } from "@/hooks/use-long-press-collection-select";
import { useCollectionUrlBuilder } from "@/lib/urls";
import { formatRelativeTimeAgo } from "@/lib/format-relative-time";
import { PinTargetMenu, collectionTargets, } from "@/components/pin/pin-target-menu";
import { usePinSlotContext } from "@/components/stream/pin-slot-context";
import { useCollectionCardPreviews } from "./use-collection-card-previews";
import "./collection-block-view.scss";
// Stable fallback slot for `usePinList` when the collection has no
// Group parent. Hooks must run unconditionally, but the snapshot
// from this slot is discarded in that branch. Held as a module
// constant so referential identity is stable across renders.
const NOOP_PIN_SCOPE = { type: "account" };
const NOOP_PIN_SURFACE = "feed";
function peekSlotClass(slot) {
    return `collection-block-view__peek collection-block-view__peek--${slot}`;
}
function peekDelayForSlot(slot) {
    switch (slot) {
        case "back-left":
        case "duo-left":
            return 0;
        case "front":
            return 60;
        case "back-right":
        case "duo-right":
            return 120;
        default:
            return 0;
    }
}
function mapEnvelopesToSlots(envelopes) {
    const n = envelopes.length;
    if (n === 0)
        return [];
    if (n === 1) {
        return [{ envelope: envelopes[0], slot: "front" }];
    }
    if (n === 2) {
        return [
            { envelope: envelopes[0], slot: "duo-left" },
            { envelope: envelopes[1], slot: "duo-right" },
        ];
    }
    return [
        { envelope: envelopes[0], slot: "front" },
        { envelope: envelopes[1], slot: "back-left" },
        { envelope: envelopes[2], slot: "back-right" },
    ];
}
export const CollectionBlockView = memoForwardRef(({ collection, onOpen }, ref) => {
    const navigate = useNavigate();
    const buildCollectionUrl = useCollectionUrlBuilder();
    const localRef = useRef(null);
    const app = useApp();
    const { envelopes, count } = useCollectionCardPreviews(collection, localRef);
    // Parent (a Group, a Primary, or undefined for standalone) drives
    // the "Pin to {parent}" option. Hide it for Primary-rooted Collections.
    const parent = collectionStore.useFindById(collection.parentCollectionId);
    const pinParentTarget = parent !== undefined && parent.type !== "primary"
        ? { id: parent.id, title: parent.title ?? "Untitled" }
        : undefined;
    const [showPinPopover, setShowPinPopover] = useState(false);
    // Resolve the single pin destination for the surrounding view.
    // Two signals drive direct-pin (skip the dropdown, single toggle):
    //   1. `PinSlotContext` from a Stream that was configured with an
    //      explicit `pinScope` (Group page, Collection detail page,
    //      /collections list). This wins when present.
    //   2. Fallback: a non-Primary parent Collection. Mirrors the
    //      Group-page heuristic for cards rendered outside a configured
    //      Stream (e.g. the omnibar filter strip).
    // When neither signal is present, the pin button falls back to
    // the multi-target `PinTargetMenu` dropdown.
    const pinSlotContext = usePinSlotContext();
    const contextScopeType = pinSlotContext?.scope.type;
    const contextScopeId = pinSlotContext?.scope.type === "collection" ? pinSlotContext.scope.id : undefined;
    const contextSurface = pinSlotContext?.surface;
    const contextLabel = pinSlotContext?.label;
    const parentId = pinParentTarget?.id;
    const parentTitle = pinParentTarget?.title;
    // Resolve the structural fields of the active pin slot first; the
    // scope object itself is memoized below so `usePinList`'s
    // subscription stays stable across renders.
    const slotScopeType = contextScopeType ?? (parentId !== undefined ? "collection" : undefined);
    const slotScopeId = contextScopeType === "collection" ? contextScopeId : parentId;
    const slotSurfaceResolved = contextSurface ?? (parentId !== undefined ? "default" : undefined);
    const useDirectPin = slotScopeType !== undefined && slotSurfaceResolved !== undefined;
    const directPinLabel = contextLabel ?? parentTitle;
    // Memoize on the structural fields, not the upstream object, so
    // an inline `pinScope={{...}}` in a parent component doesn't make
    // every render re-subscribe `usePinList`.
    const slotScope = useMemo(() => {
        if (slotScopeType === "collection" && slotScopeId !== undefined) {
            return { type: "collection", id: slotScopeId };
        }
        return NOOP_PIN_SCOPE;
    }, [slotScopeType, slotScopeId]);
    const slotSurface = slotSurfaceResolved ?? NOOP_PIN_SURFACE;
    const slotPins = usePinList(slotScope, slotSurface);
    const isPinnedHere = useDirectPin &&
        slotPins.some((p) => p.type === "collection" && p.id === collection.id);
    const handleDirectPin = useCallback(() => {
        if (useDirectPin === false || slotSurfaceResolved === undefined)
            return;
        void togglePinWithSync(slotScope, slotSurfaceResolved, {
            type: "collection",
            id: collection.id,
        }).then((result) => {
            if (result.ok === false && result.reason === "max") {
                showToast.warning(`You cannot pin more than ${MAX_PINS_PER_SURFACE} items here`);
            }
        });
    }, [useDirectPin, slotScope, slotSurfaceResolved, collection.id]);
    // Long-press → enter collection-multiselect (with this card seeded)
    // unless we're already in some multiselect mode.
    useLongPressCollectionSelect(collection.id, localRef);
    // Subscribe to the slices we need to switch between "open" and
    // "toggle selection" click behavior. We deliberately avoid pulling
    // the whole `collectionSelection` set as a snapshot — Zustand re-
    // emits a new Set on every mutation, and selecting `s.collectionSelection`
    // would re-render every card on every selection change. Instead we
    // derive a boolean per card.
    //
    // Note: we intentionally do NOT subscribe to `s.ui.isMultiselectMode`
    // here. The flag flipping (e.g. when the user enters block-multiselect
    // from elsewhere in the app) would otherwise re-render every visible
    // collection card. `isCollectionMultiselect` below already collapses
    // both halves of the check into a single stable boolean — false when
    // collections aren't the active selection target, so this card pays
    // zero re-render cost for block- or stack-multiselect mode flips.
    // The pin button's "hide during multiselect" visual is handled in
    // CSS via the `.app-screen--multiselect` ancestor class.
    const isSelected = app.useStore((s) => s.collectionSelection.has(collection.id));
    const isCollectionMultiselect = app.useStore((s) => s.ui.isMultiselectMode && s.collectionSelection.size > 0);
    const mergedRef = useCallback((node) => {
        localRef.current = node;
        if (typeof ref === "function")
            ref(node);
        else if (ref)
            ref.current = node;
    }, [ref]);
    const handleClick = useCallback((e) => {
        e.stopPropagation();
        if (isCollectionMultiselect) {
            app.toggleCollectionSelection(collection.id);
            return;
        }
        if (onOpen) {
            onOpen(collection);
            return;
        }
        navigate(buildCollectionUrl(collection));
    }, [app, collection, isCollectionMultiselect, navigate, onOpen, buildCollectionUrl]);
    const title = collection.title || "Untitled";
    const countLabel = count === 1 ? "1 item" : `${count} items`;
    // Append a relative-time suffix when the list endpoint surfaced a
    // `lastAddedAt` (i.e. the collection has at least one block). Empty
    // collections fall back to the bare count — `lastAddedAt` is the
    // most-recent-block timestamp, so there's nothing meaningful to
    // render when the count is 0.
    const lastAddedLabel = collection.lastAddedAt
        ? formatRelativeTimeAgo(collection.lastAddedAt)
        : "";
    const metaLabel = lastAddedLabel ? `${countLabel} · ${lastAddedLabel}` : countLabel;
    const slots = mapEnvelopesToSlots(envelopes);
    const collectionColors = getCollectionCardColors(collection.id);
    const style = {
        "--collection-shell-surface": collectionColors.shellSurface,
        "--collection-shell-surface-hover": collectionColors.shellSurfaceHover,
        "--collection-footer-surface": collectionColors.footerSurface,
        "--collection-border": collectionColors.border,
        "--collection-border-hover": collectionColors.borderHover,
        "--collection-footer-divider": collectionColors.footerDivider,
        "--collection-title": collectionColors.title,
        "--collection-meta": collectionColors.meta,
    };
    // `isCollectionMultiselect` already encodes `isMultiselectMode === true`
    // (see the selector above); no need to re-check the broader flag.
    const showSelectionOverlay = isCollectionMultiselect;
    const overlayClasses = [
        "collection-block-view__select-overlay",
        isSelected ? "collection-block-view__select-overlay--checked" : "",
    ]
        .filter(Boolean)
        .join(" ");
    const cardClasses = [
        "collection-block-view",
        isSelected ? "collection-block-view--selected" : "",
    ]
        .filter(Boolean)
        .join(" ");
    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick(e);
        }
    }, [handleClick]);
    return (_jsxs("div", { ref: mergedRef, role: "button", tabIndex: 0, className: cardClasses, style: style, onClick: handleClick, onKeyDown: handleKeyDown, "aria-label": `Open collection ${title}`, "aria-pressed": isCollectionMultiselect ? isSelected : undefined, children: [showSelectionOverlay ? (_jsx("div", { className: overlayClasses, "aria-hidden": true, children: _jsx("div", { className: "collection-block-view__select-checkbox", children: isSelected ? _jsx(FaIcon, { icon: faCheck }) : null }) })) : null, _jsx("div", { className: "collection-block-view__stack", "aria-hidden": true, children: count === 0 ? null : slots.length > 0 ? (slots.map(({ envelope, slot }) => (_jsx("div", { className: peekSlotClass(slot), style: {
                        "--collection-peek-delay": `${peekDelayForSlot(slot)}ms`,
                    }, children: _jsx("div", { className: "collection-block-view__peek-inner", children: _jsx(BlockMiniCapture, { envelope: envelope }) }) }, envelope.block.id)))) : (_jsxs(_Fragment, { children: [_jsx("div", { className: peekSlotClass("back-left"), children: _jsx("div", { className: "collection-block-view__peek-inner", children: _jsx("div", { className: "collection-block-view__peek-slab" }) }) }), _jsx("div", { className: peekSlotClass("back-right"), children: _jsx("div", { className: "collection-block-view__peek-inner", children: _jsx("div", { className: "collection-block-view__peek-slab" }) }) }), _jsx("div", { className: peekSlotClass("front"), children: _jsx("div", { className: "collection-block-view__peek-inner", children: _jsx("div", { className: "collection-block-view__peek-slab" }) }) })] })) }), _jsxs("div", { className: "collection-block-view__footer", children: [_jsx("span", { className: "collection-block-view__title", children: title }), _jsx("span", { className: "collection-block-view__meta", title: collection.lastAddedAt ?? undefined, children: metaLabel })] }), _jsx("div", { className: `collection-block-view__pin-anchor ${isPinnedHere ? "collection-block-view__pin-anchor--active" : ""}`, onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), children: useDirectPin ? (_jsx("button", { type: "button", className: `collection-block-view__pin-btn ${isPinnedHere ? "collection-block-view__pin-btn--active" : ""}`, "aria-label": isPinnedHere
                        ? `Unpin from ${directPinLabel ?? "this view"}`
                        : `Pin to ${directPinLabel ?? "this view"}`, "aria-pressed": isPinnedHere, onClick: (e) => {
                        e.stopPropagation();
                        handleDirectPin();
                    }, children: _jsx(FaIcon, { icon: faThumbtack }) })) : (_jsxs(_Fragment, { children: [showPinPopover ? (_jsx(PinTargetMenu, { item: { type: "collection", id: collection.id }, targets: collectionTargets(pinParentTarget), onClose: () => setShowPinPopover(false) })) : undefined, _jsx("button", { type: "button", className: "collection-block-view__pin-btn", "aria-label": "Pin collection...", onClick: (e) => {
                                e.stopPropagation();
                                setShowPinPopover((prev) => prev === false);
                            }, children: _jsx(FaIcon, { icon: faThumbtack }) })] })) })] }));
});
